// ./src/utils/api/PlayListItemService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { PlayList } from "./PlayListService";
import { ContentBlock } from "./ContentBlockService";

// GraphQL queries
const PLAYLISTITEM_LIST_QUERY = gql`
  query PlayListItemList(
    $filter: PlayListItemFilter
    $params: PaginationAndSorting
  ) {
    playListItemList(filter: $filter, params: $params) {
      code
      data {
        id
        playListId
        contentBlockId
        durationSeconds
        startDatetime
        endDatetime
        startTime
        endTime
        recurrencePattern
        recurrenceDays
        orderNo
        status
        createdBy
        createdAt
        contentBlock {
          id
          name
          contentType
          durationSeconds
          startDatetime
          endDatetime
          startTime
          endTime
          recurrencePattern
          recurrenceDays
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const PLAYLISTITEM_GET_QUERY = gql`
  query PlayListItemGet($id: ID) {
    playListItemGet(id: $id) {
      code
      data {
        id
        playListId
        contentBlockId
        durationSeconds
        startDatetime
        endDatetime
        startTime
        endTime
        recurrencePattern
        recurrenceDays
        orderNo
        status
        createdBy
        createdAt
        playList {
          name
          description
          status
        }
        contentBlock {
          id
          name
          contentType
          durationSeconds
          startDatetime
          endDatetime
          startTime
          endTime
          recurrencePattern
          recurrenceDays
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const PLAYLISTITEM_GET_MANY_QUERY = gql`
  query PlayListItemGetMany($ids: [ID]) {
    playListItemGetMany(ids: $ids) {
      code
      data {
        id
        playListId
        contentBlockId
        durationSeconds
        startDatetime
        endDatetime
        startTime
        endTime
        recurrencePattern
        recurrenceDays
        orderNo
        status
        createdBy
        createdAt
        playList {
          id
          accountId
          name
          description
          status
        }
        contentBlock {
          id
          name
          contentType
          durationSeconds
          startDatetime
          endDatetime
          startTime
          endTime
          recurrencePattern
          recurrenceDays
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const PLAYLISTITEM_CREATE_MUTATION = gql`
  mutation PlayListItemCreate($params: PlayListItemInput) {
    playListItemCreate(params: $params) {
      code
      data {
        id
        playListId
        contentBlockId
        durationSeconds
        startDatetime
        endDatetime
        startTime
        endTime
        recurrencePattern
        recurrenceDays
        orderNo
        status
        createdBy
        createdAt
        playList {
          id
          accountId
          name
          description
          status
        }
        contentBlock {
          id
          name
          contentType
          durationSeconds
          startDatetime
          endDatetime
          startTime
          endTime
          recurrencePattern
          recurrenceDays
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const PLAYLISTITEM_UPDATE_MUTATION = gql`
  mutation PlayListItemUpdate(
    $where: PlayListItemWhereInput
    $params: PlayListItemInput
  ) {
    playListItemUpdate(where: $where, params: $params) {
      code
      data {
        id
        playListId
        contentBlockId
        durationSeconds
        startDatetime
        endDatetime
        startTime
        endTime
        recurrencePattern
        recurrenceDays
        orderNo
        status
        createdBy
        createdAt
        playList {
          id
          accountId
          name
          description
          status
        }
        contentBlock {
          id
          name
          contentType
          durationSeconds
          startDatetime
          endDatetime
          startTime
          endTime
          recurrencePattern
          recurrenceDays
          status
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const PLAYLISTITEM_REMOVE_MUTATION = gql`
  mutation PlayListItemRemove($where: PlayListItemWhereInput) {
    playListItemRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const PLAYLISTITEM_REMOVE_MANY_MUTATION = gql`
  mutation PlayListItemRemoveMany($where: [PlayListItemWhereInput]) {
    playListItemRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface PlayListItem {
  id?: string | undefined;
  playListId?: string | undefined;
  contentBlockId?: string | undefined;
  durationSeconds?: number | undefined;
  startDatetime?: string | undefined;
  endDatetime?: string | undefined;
  startTime?: string | undefined;
  endTime?: string | undefined;
  recurrencePattern?: string | undefined;
  recurrenceDays?: string | undefined;
  orderNo?: number | undefined;
  status?: number;
  createdBy?: string | undefined;
  createdAt?: string | undefined;
  playList?: PlayList | null | undefined;
  contentBlock?: ContentBlock | null | undefined;
}

export interface PlayListItemInput {
  playListId?: string | null | undefined;
  contentBlockId?: string | null | undefined;
  durationSeconds?: number | null | undefined;
  startDatetime?: string | null | undefined;
  endDatetime?: string | null | undefined;
  startTime?: string | null | undefined;
  endTime?: string | null | undefined;
  recurrencePattern?: string | null | undefined;
  recurrenceDays?: string | null | undefined;
  blockSettings?: string | null | undefined;
  orderNo?: number | null | undefined;
  status?: number | null | undefined;
}

export interface PlayListItemFilter {
  id?: string[] | null | undefined;
  playListId?: string[] | null | undefined;
  contentBlockId?: string[] | null | undefined;
  searchKeyword?: string | null | undefined;
  status?: number[] | null | undefined;
}

export interface PlayListItemWhereInput {
  id?: string | undefined;
}

/**
 * PlayListItem service that handles all playlist item-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class PlayListItemService {
  /**
   * Get a list of playlist items with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: PlayListItemFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(PLAYLISTITEM_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single playlist item by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(PLAYLISTITEM_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple playlist items by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(PLAYLISTITEM_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Create a new playlist item
   */
  static async create(params: PlayListItemInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      PLAYLISTITEM_CREATE_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing playlist item
   */
  static async update(
    where: PlayListItemWhereInput,
    params: PlayListItemInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      PLAYLISTITEM_UPDATE_MUTATION,
      {
        where,
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete a playlist item
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      PLAYLISTITEM_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple playlist items
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      PLAYLISTITEM_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
